// ULLs EGNE ikoner — jegertroppens sensorarter, pluss appens egne begreper.
//
// Skallet (`grep`, `dokk`, `popout`, `kryss`), de universelle tilstandene
// (`info`, `varsel`, `sjekk`, `last`, `nede`) og de begrepene enhver sensorflate
// har (`klokke`, `peiling`, `maal`, `kart`, `søk`, `kamera`) ligger i
// `@skade/sensorflate`. Grensa går ved: BESKRIVER ikonet en sensorART eller et
// domeneobjekt? Da er det ULLs.
//
// `kamera` er felles og finnes IKKE her — både en kystpost og et jegerlag har
// kamera, og pakken eier det. De fem artsikonene under er derimot meningsløse
// utenfor en bakkesensor.
//
// Ikonene bygges med byggeklossene fra pakken (`ikonSvg`, `bane`, `rekt`,
// `sirkel`), slik at strekbredde, hjørner og viewBox er identiske med resten av
// systemet. Reglene fra UX_DESIGNSYSTEM §6 gjelder uendret: ALDRI emoji, aldri
// Unicode-glyfer blandet med SVG, og ingen `innerHTML` — nodene bygges med
// `createElementNS`.
import {
  bane, ikonSvg, rekt, sirkel, ikon as flateIkon, type Ikonnavn as FlateIkonnavn,
} from '@skade/sensorflate';

export const ULL_IKONER = {
  /** Seismisk: bakkelinje med en rystelse under. Rundstrålende ringer bevisst
   *  UTELATT — ikonet skal ikke antyde en retning geofonen ikke har. */
  seismisk: () => ikonSvg(bane('M2 7h16'), bane('M3 13l2.5-4 2.5 8 2.5-8 2.5 6 2.5-4 2 2')),
  /** PIR: sensorhus med to stråler (dobbelstråle er den ENESTE UGS-en som bærer
   *  retning, og det er de to strålene som gjør det). */
  pir: () => ikonSvg(rekt(2.5, 6.5, 5, 7, 1.5), bane('M8 9h9'), bane('M8 12h9'), bane('M15 7l2.5 2-2.5 2')),
  /** Magnetisk: hestesko-magnet med to bein. */
  magnetisk: () => ikonSvg(bane('M6 16V9a4 4 0 0 1 8 0v7'), bane('M4 16h4'), bane('M12 16h4')),
  /** Akustisk: mikrofon med lydbuer til BEGGE sider — én mikrofon peiler ikke. */
  akustisk: () => ikonSvg(rekt(8.5, 3, 3, 8, 1.5), bane('M10 11v5'), bane('M7 17h6'),
    bane('M5.5 6a5 5 0 0 0 0 6'), bane('M14.5 6a5 5 0 0 1 0 6')),
  /** Brytertråd: strek mellom to fester, brutt på midten. */
  brytertråd: () => ikonSvg(bane('M2 10h6'), bane('M12 10h6'), sirkel(2.5, 10, 1), sirkel(17.5, 10, 1),
    bane('M8.5 7.5l1.5 2.5-1.5 2.5'), bane('M11.5 7.5L10 10l1.5 2.5')),
  /** Sekvens: tre punkt på en linje med pil — appens hovedbegrep. */
  sekvens: () => ikonSvg(bane('M3 15L17 6'), sirkel(4, 14.5, 1.6), sirkel(9.5, 11, 1.6), sirkel(15, 7.5, 1.6),
    bane('M14 4.5L17.5 6L16 9')),
  /** Pil: «denne utløste FØR den neste». Brukes bare der rekkefølgen faktisk er
   *  bestemt — se `tegnKjede` i sekvensanalysen. */
  pil: () => ikonSvg(bane('M3 10h13'), bane('M12 6l4 4-4 4')),
  /** Spørsmålstegn i en ring: rekkefølgen mellom to utløsninger er IKKE bestemt.
   *  Eget ikon og ikke et Unicode-tegn, slik at kjedestripa er ren SVG (§6). */
  ubestemt: () => ikonSvg(sirkel(10, 10, 7), bane('M7.8 7.6a2.3 2.3 0 1 1 2.2 2.9v1.2'), bane('M10 14.6v.2')),
  /** Dekningshull: dekket–hull–dekket langs en akse. */
  hull: () => ikonSvg(bane('M2 10h5'), bane('M13 10h5'), bane('M7 6.5v7'), bane('M13 6.5v7')),
  /** Sperrepunkt: tråd på tvers av en akse. */
  sperre: () => ikonSvg(bane('M2 14h16'), bane('M7 4l6 12'), bane('M13 4L7 16')),
  /** Sending (EMCON): antenne som stråler. Brukes BÅDE på bryteren og i
   *  varselstripa, så det ikke kan misforstås hvilken tilstand den viser. */
  sending: () => ikonSvg(bane('M10 9v8'), sirkel(10, 7, 1.4), bane('M6.5 10a5 5 0 0 1 0-6'),
    bane('M13.5 4a5 5 0 0 1 0 6'), bane('M7 17h6')),
  /** Sending AV: samme antenne, overstrøket. Aldri farge alene (§ mørke/NVG). */
  'sending-av': () => ikonSvg(bane('M10 9v8'), sirkel(10, 7, 1.4), bane('M7 17h6'), bane('M3.5 3.5l13 13')),
  /** Melding: ark med linjer — stridsmeldingens åtte linjer. */
  melding: () => ikonSvg(rekt(4, 3, 12, 14, 2), bane('M7 7.5h6'), bane('M7 10.5h6'), bane('M7 13.5h4')),
  /** Batteri — den ene sliteflaten en utlagt sensor har. */
  batteri: () => ikonSvg(rekt(2.5, 7, 13, 6, 1.5), bane('M17 9.5v1'), bane('M5 9.5v1')),
} as const;

export type UllIkonnavn = keyof typeof ULL_IKONER;

/** Alle ikoner ULL kan be om: flatens felles + jegertroppens egne. */
export type Ikonnavn = FlateIkonnavn | UllIkonnavn;

/**
 * Hent et ikon. Alltid en ny node — SVG-er kan ikke deles mellom to steder.
 *
 * Ett oppslag for begge sett, slik at kallstedene ikke trenger vite HVOR ikonet
 * er definert. Flytter et ikon seg mellom ULL og pakken (f.eks. hvis NJORD også
 * får akustiske bakkesensorer), endres bare denne fila.
 */
export function ikon(navn: Ikonnavn): SVGSVGElement {
  const eget = (ULL_IKONER as Record<string, (() => SVGSVGElement) | undefined>)[navn];
  return eget ? eget() : flateIkon(navn as FlateIkonnavn);
}

/**
 * Ikonet for en sensorart. Navnene i `ULL_IKONER` speiler `UgsArt` med vilje, med
 * det ene unntaket `kamera` som er pakkens. Tabellen er skrevet ut i stedet for
 * å slå opp arten direkte, nettopp fordi det unntaket finnes.
 */
export const ART_IKON = {
  akustisk: 'akustisk',
  seismisk: 'seismisk',
  pir: 'pir',
  kamera: 'kamera',
  brytertråd: 'brytertråd',
  magnetisk: 'magnetisk',
} as const satisfies Record<string, Ikonnavn>;
